import React from "react";
import Draggable from "react-draggable";
import swal from "sweetalert";
import * as cocoSsd from "@tensorflow-models/coco-ssd";
import "@tensorflow/tfjs";
import "./Detections.css";

export default class Detection extends React.Component {
  videoRef = React.createRef();
  canvasRef = React.createRef();

  state = {
    hasSubmitted: false,
    cheatCount: 0,
  };

  popupCooldowns = {};
  popupCooldownMs = 3000;
  isPopupOpen = false;

  baselineBox = null;

  objectTimers = {
    person: null,
    cellPhone: null,
    book: null,
    laptop: null,
    tv: null,
  };

  objectCheatCounted = {
    person: false,
    cellPhone: false,
    book: false,
    laptop: false,
    tv: false,
    backgroundNoise: false,
  };

  lastFrame = null;
  idleTimer = null;
  lookAwayTimer = null;
  lastHeadX = null;
  lastHeadFrame = null;
  eyeCloseTimer = null;
  
  // ✅ Duration-based noise detection timer
  noiseStartTime = null;
  noiseDurationThreshold = 5000; // 5 seconds in milliseconds

  // ✅ Store cheat reasons using scoped key
  storeCheatReason = (reason) => {
    const key = localStorage.getItem('current_cheat_key') || "cheat_reasons_string";
    const timestamp = new Date().toLocaleString();
    const previous = localStorage.getItem(key) || "";
    const entry = `${reason} | ${timestamp}\n`;
    localStorage.setItem(key, previous + entry);
  };

  incrementCheatCount = (callback) => {
    this.setState(
      (prev) => ({ cheatCount: prev.cheatCount + 1 }),
      callback
    );
  };

  componentDidMount() {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      const webCamPromise = navigator.mediaDevices
        .getUserMedia({
          audio: false,
          video: { facingMode: "user", width: 500, height: 300 },
        })
        .then((stream) => {
          window.stream = stream;
          this.videoRef.current.srcObject = stream;
          return new Promise((resolve) => {
            this.videoRef.current.onloadedmetadata = () => {
              if (this._isUnmounted) return resolve();
              const v = this.videoRef.current;
              const c = this.canvasRef.current;
              if (!v || !c) return resolve();
              try {
                v.play();
              } catch {}
              c.width = v.videoWidth || 500;
              c.height = v.videoHeight || 300;
              resolve();
            };
          });
        });

      const modelPromise = cocoSsd.load();

      Promise.all([modelPromise, webCamPromise])
        .then(([model]) => {
          this.detectFrame(this.videoRef.current, model);
          this.setupIdleDetection();
        })
        .catch((error) => console.error(error));

      // ✅ Microphone noise detection with duration-based filtering
      navigator.mediaDevices
        .getUserMedia({ audio: true, video: false })
        .then((stream) => {
          const audioContext =
            new (window.AudioContext || window.webkitAudioContext)();
          const source = audioContext.createMediaStreamSource(stream);
          const analyser = audioContext.createAnalyser();
          analyser.fftSize = 512;
          source.connect(analyser);

          const dataArray = new Uint8Array(analyser.frequencyBinCount);

          const checkAudio = () => {
            analyser.getByteFrequencyData(dataArray);
            const avg =
              dataArray.reduce((a, b) => a + b, 0) / dataArray.length;

            // ✅ Combined approach: Higher threshold (60) + Duration check (5s)
            // - Typing/clicks typically: 30-50 (filtered by threshold)
            // - Human speech typically: 60-100 (triggers detection)
            const trigger = 60;
            const reset = 30;

            // ✅ Duration-based detection: Only flag if noise sustained for 5+ seconds
            if (avg > trigger) {
              // Start timer if not already started
              if (!this.noiseStartTime) {
                this.noiseStartTime = Date.now();
              } else {
                // Check if noise has been sustained long enough
                const elapsed = Date.now() - this.noiseStartTime;
                if (elapsed >= this.noiseDurationThreshold && !this.objectCheatCounted["backgroundNoise"]) {
                  this.objectCheatCounted["backgroundNoise"] = true;
                  this.incrementCheatCount(() => {
                    this.storeCheatReason("Background Noise Detected");
                    this.showPopup(
                      "Background Noise Detected",
                      "Action Recorded",
                      3000,
                      "backgroundNoise"
                    );
                  });
                }
              }
            } else if (avg <= reset) {
              // Reset timer and flag when noise drops below threshold
              this.noiseStartTime = null;
              this.objectCheatCounted["backgroundNoise"] = false;
            }

            requestAnimationFrame(checkAudio);
          };

          checkAudio();
        })
        .catch((err) => console.error("Microphone access error:", err));
    }
  }

  componentWillUnmount() {
    this._isUnmounted = true;
    if (this._rafId) cancelAnimationFrame(this._rafId);
    clearTimeout(this.idleTimer);
    clearTimeout(this.lookAwayTimer);
    clearTimeout(this.eyeCloseTimer);
    
    // ✅ Clear noise timer on unmount
    this.noiseStartTime = null;

    try {
      const v = this.videoRef.current;
      if (v) v.onloadedmetadata = null;
      const stream = v?.srcObject;
      stream?.getTracks?.().forEach((t) => t.stop());
    } catch {}
  }

  setupIdleDetection = () => {
    const checkIdle = () => {
      if (this.videoRef.current) {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = this.videoRef.current.videoWidth;
        canvas.height = this.videoRef.current.videoHeight;

        ctx.drawImage(this.videoRef.current, 0, 0);
        const currentFrame = ctx.getImageData(
          0,
          0,
          canvas.width,
          canvas.height
        ).data;

        if (this.lastFrame) {
          let diff = 0;
          for (let i = 0; i < currentFrame.length; i += 4) {
            diff += Math.abs(currentFrame[i] - this.lastFrame[i]);
          }

          if (diff < 100000) {
            if (!this.idleTimer) {
              this.idleTimer = setTimeout(() => {
                this.incrementCheatCount(() => {
                  this.storeCheatReason("Idle - No Motion Detected");
                  this.showPopup(
                    "No Motion Detected",
                    "Possible Cheating",
                    3000,
                    "idle"
                  );
                });
                this.idleTimer = null;
              }, 120000);
            }
          } else {
            clearTimeout(this.idleTimer);
            this.idleTimer = null;
          }
        }
        this.lastFrame = currentFrame;
      }

      requestAnimationFrame(checkIdle);
    };

    checkIdle();
  };

  detectFrame = (video, model) => {
    if (this._isUnmounted) return;
    if (!video || video.readyState < 2) {
      this._rafId = requestAnimationFrame(() =>
        this.detectFrame(video, model)
      );
      return;
    }

    model
      .detect(video)
      .then((predictions) => {
        if (this._isUnmounted) return;
        if (this.canvasRef.current) {
          this.renderPredictions(predictions);
          this._rafId = requestAnimationFrame(() =>
            this.detectFrame(video, model)
          );
        }
      })
      .catch(() => {
        this._rafId = requestAnimationFrame(() =>
          this.detectFrame(video, model)
        );
      });
  };

  canShowPopup = (key) => {
    const now = Date.now();
    const last = this.popupCooldowns[key] || 0;
    if (now - last < this.popupCooldownMs) return false;
    this.popupCooldowns[key] = now;
    return true;
  };

  showPopup = (title, text, duration = 3000, key = undefined) => {
    if (this.isPopupOpen) return;
    if (!this.canShowPopup(key)) return;

    // Check if tab switch modal or any other modal is already open
    try {
      if (sessionStorage.getItem("bt_skip_tab_switch") === "true") {
        // Tab switch modal is open, don't show proctoring popup
        // Just increment count (already done in caller)
        return;
      }
    } catch {}

    this.isPopupOpen = true;
    // Set flag to prevent tab switch modal while proctoring popup is showing
    try {
      sessionStorage.setItem("bt_proctoring_popup_open", "true");
    } catch {}

    // Create styled content matching tab switch modal design
    const container = document.createElement('div');
    container.className = 'text-center';
    container.innerHTML = `
      <div class="flex items-center justify-center w-12 h-12 mx-auto mb-4 text-3xl text-yellow-600 bg-yellow-100 rounded-full">
        ⚠️
      </div>
      <h3 class="mb-5 text-lg font-semibold text-gray-900">
        ${title}
      </h3>
      <div class="px-4 py-3 mb-4 text-sm text-center bg-gray-100 border-yellow-500 rounded">
        <p class="mt-1 text-gray-600">
          ${text}
        </p>
      </div>
      <p class="mb-3 text-base text-gray-700">
        Total Cheats: <strong>${this.state.cheatCount}</strong>
      </p>
      <p class="text-sm text-gray-600">
        Please follow test guidelines.
      </p>
    `;

    swal({
      content: container,
      buttons: false,
      timer: duration,
      closeOnClickOutside: false,
      closeOnEsc: false,
    }).then(() => {
      this.isPopupOpen = false;
      // Clear flag when popup closes
      try {
        sessionStorage.removeItem("bt_proctoring_popup_open");
      } catch {}
    });
  };

  handleObjectState = (obj, detected, popupMessage) => {
    const key =
      obj === "cell phone"
        ? "cellPhone"
        : obj === "book"
        ? "book"
        : obj === "laptop"
        ? "laptop"
        : obj === "tv"
        ? "tv"
        : "person";

    if (detected) {
      if (!this.objectTimers[key]) {
        this.objectTimers[key] = Date.now();
        this.objectCheatCounted[key] = false;
      } else {
        const elapsed = (Date.now() - this.objectTimers[key]) / 1000;
        if (elapsed >= 3 && !this.objectCheatCounted[key]) {
          this.objectCheatCounted[key] = true;
          this.incrementCheatCount(() => {
            this.storeCheatReason(popupMessage);
            this.showPopup(popupMessage, "Action Recorded", 3000, key);
          });
        }
      }
    } else {
      this.objectTimers[key] = null;
      this.objectCheatCounted[key] = false;
    }
  };

  detectUserChange = (personPredictions) => {
    if (personPredictions.length !== 1) return;

    const [x, y, w, h] = personPredictions[0].bbox;

    if (!this.baselineBox) {
      this.baselineBox = { x, y, width: w, height: h };
      return;
    }

    const dx = Math.abs(x - this.baselineBox.x);
    const dy = Math.abs(y - this.baselineBox.y);
    const dw = Math.abs(w - this.baselineBox.width);
    const dh = Math.abs(h - this.baselineBox.height);

    const areaChange = Math.abs(w * h - this.baselineBox.width * this.baselineBox.height);

    if (
      dx > w * 0.5 ||
      dy > h * 0.5 ||
      dw > w * 0.5 ||
      dh > h * 0.5 ||
      areaChange > w * h * 0.5
    ) {
      this.incrementCheatCount(() => {
        this.storeCheatReason("Possible Face Swap / User Change");
        this.showPopup(
          "Face Not Detected",
          "Possible Cheating",
          3000,
          "faceSwap"
        );
      });

      this.baselineBox = { x, y, width: w, height: h };
    }
  };

  renderPredictions = (predictions) => {
    const ctx = this.canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    const monitored = ["person", "cell phone", "book", "laptop", "tv"];
    const filtered = predictions.filter((p) => monitored.includes(p.class));

    const personPred = filtered.filter((p) => p.class === "person");

    if (personPred.length === 0) {
      if (!this.lookAwayTimer) {
        this.lookAwayTimer = setTimeout(() => {
          this.incrementCheatCount(() => {
            this.storeCheatReason("User Not Visible");
            this.showPopup(
              "User Not Visible",
              "Possible Cheating",
              3000,
              "userNotVisible"
            );
          });
          this.lookAwayTimer = null;
        }, 3000);
      }
      this.handleObjectState("person", false, "Face Not Detected");
    } else if (personPred.length > 1) {
      this.incrementCheatCount(() => {
        this.storeCheatReason("Multiple Users Detected");
        this.showPopup(
          "Multiple Users Detected",
          "Possible Cheating",
          3000,
          "multipleUsers"
        );
      });
    } else {
      this.detectUserChange(personPred);

      const [x, y, w, h] = personPred[0].bbox;
      const cx = x + w / 2;
      const cy = y + h / 2;

      if (w < 50 || h < 50) {
        if (!this.lookAwayTimer) {
          this.lookAwayTimer = setTimeout(() => {
            this.incrementCheatCount(() => {
              this.storeCheatReason("User Too Far");
              this.showPopup(
                "User Too Far",
                "Possible Cheating",
                3000,
                "userTooFar"
              );
            });
            this.lookAwayTimer = null;
          }, 3000);
        }
      } else {
        const safeX = this.videoRef.current.videoWidth * 0.2;
        const safeY = this.videoRef.current.videoHeight * 0.2;

        if (
          cx < safeX ||
          cx > this.videoRef.current.videoWidth - safeX ||
          cy < safeY ||
          cy > this.videoRef.current.videoHeight - safeY
        ) {
          if (!this.lookAwayTimer) {
            this.lookAwayTimer = setTimeout(() => {
              this.incrementCheatCount(() => {
                this.storeCheatReason("User Off-Center");
                this.showPopup(
                  "User Off-Center",
                  "Possible Cheating",
                  3000,
                  "offCenter"
                );
              });
              this.lookAwayTimer = null;
            }, 3000);
          }
        } else {
          clearTimeout(this.lookAwayTimer);
          this.lookAwayTimer = null;
        }
      }

      // Person properly detected - reset timers without counting as cheat
      clearTimeout(this.lookAwayTimer);
      this.lookAwayTimer = null;
    }

    // ✅ Object detection reasons stored automatically in handleObjectState()
    ["cell phone", "book", "laptop", "tv"].forEach((obj) => {
      const detected = filtered.some((p) => p.class === obj);
      this.handleObjectState(obj, detected, `${obj} Detected`);
    });

    sessionStorage.setItem("count_facedetect", this.state.cheatCount);

    // ✅ Auto Submit
    if (
      this.state.cheatCount >= this.props.max_cheat_count &&
      !this.state.hasSubmitted
    ) {
      this.setState({ hasSubmitted: true }, () => {
        this.storeCheatReason("Auto Submitted - Cheat Limit Exceeded");
        this.showPopup("Auto Submitted", "Cheating Limit Exceeded", 2000);
        this.props.handleSubmit(false, true);
      });
    }
  };

  render() {
    return (
      <div className="container" style={{ pointerEvents: "none" }}>
        <div style={{ pointerEvents: "none" }}>
          <Draggable>
            <video
              className="video"
              autoPlay
              playsInline
              muted
              ref={this.videoRef}
              width="200"
              height="150"
            />
          </Draggable>
        </div>

        <canvas
          className="hidden"
          ref={this.canvasRef}
          width="200"
          height="150"
        />
      </div>
    );
  }
}